export type VideoEmbed = {
  src: string;
  title: string;
  host: 'YouTube' | 'Vimeo' | 'TikTok' | 'Instagram' | 'X';
  poster?: string;
};

/** Resolve public provider links to trusted player URLs. */
export function publicVideoEmbed(input: string): VideoEmbed | null {
  let parsed: URL;
  try {
    parsed = new URL(input.trim());
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' || parsed.username || parsed.password || parsed.port)
    return null;
  const host = parsed.hostname.replace(/^www\./, '');
  const parts = parsed.pathname.split('/').filter(Boolean);
  if (
    host === 'youtu.be' ||
    host === 'youtube.com' ||
    host === 'm.youtube.com' ||
    host === 'youtube-nocookie.com'
  ) {
    const id =
      host === 'youtu.be'
        ? parts[0]
        : parsed.pathname === '/watch'
          ? parsed.searchParams.get('v')
          : parts[0] === 'embed' || parts[0] === 'shorts' || parts[0] === 'live'
            ? parts[1]
            : null;
    if (!id || !/^[\w-]{11}$/.test(id)) return null;
    return {
      src: `https://www.youtube-nocookie.com/embed/${id}?rel=0`,
      host: 'YouTube',
      title: 'YouTube video',
      poster: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
    };
  }
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const id =
      host === 'player.vimeo.com' ? (parts[0] === 'video' ? parts[1] : null) : parts.at(-1);
    if (!id || !/^\d+$/.test(id)) return null;
    return { src: `https://player.vimeo.com/video/${id}`, host: 'Vimeo', title: 'Vimeo video' };
  }
  if (
    host === 'x.com' ||
    host === 'twitter.com' ||
    host === 'mobile.twitter.com' ||
    host === 'mobile.x.com'
  ) {
    const status = parts.indexOf('status');
    const id = status >= 0 ? parts[status + 1] : '';
    if (!id || !/^\d{5,25}$/.test(id)) return null;
    return {
      src: `https://platform.twitter.com/embed/Tweet.html?id=${id}&dnt=true&theme=dark`,
      title: 'Post on X',
      host: 'X',
    };
  }
  if (host === 'instagram.com') {
    const kind = parts[0];
    const code = parts[1] ?? '';
    if ((kind === 'reel' || kind === 'p' || kind === 'tv') && /^[\w-]{5,30}$/.test(code)) {
      return {
        src: `https://www.instagram.com/${kind}/${code}/embed`,
        title: 'Instagram video',
        host: 'Instagram',
      };
    }
  }
  if (host === 'tiktok.com' || host === 'm.tiktok.com') {
    const video = parts.indexOf('video');
    const id = video >= 0 ? parts[video + 1] : '';
    if (!id || !/^\d{5,25}$/.test(id)) return null;
    return {
      src: `https://www.tiktok.com/embed/v2/${id}`,
      title: 'TikTok video',
      host: 'TikTok',
    };
  }
  return null;
}
